// src/components/AddAutomationRuleForm.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Divider,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Card,
  CardContent,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  ListItemIcon,
  ListItemText,
  MenuItem as MItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import BoltIcon from "@mui/icons-material/Bolt";

import DynamicFields from "./DynamicFields";
import { useAutomationCatalog } from "../hooks/useAutomationRules";
import { useAuthContext } from "../context/AuthContext";
import { fetchData } from "../api/client";


const steps = ["Objeto", "Evento", "Condições", "Ações", "Resumo"];

/* ================= Helpers UI (chips) ================= */
const getImpactAlert = (count) => {
  if (count === 0) {
    return {
      severity: "info",
      title: "Nenhuma solicitação corresponde ao filtro neste momento",
      text: `Com os filtros atuais, nenhuma solicitação se enquadra agora.

Isso não significa que a regra não funcionará.
Ela será executada conforme o evento configurado:
• Em eventos pontuais (ex.: alteração de solicitação), apenas a solicitação que disparar o evento será avaliada.
• Em eventos recorrentes (ex.: a cada 30 minutos), novas solicitações que atendam aos critérios poderão ser processadas automaticamente.`,
    };
  }

  if (count > 100) {
    return {
      severity: "error",
      title: "⚠️ Abrangência muito alta do filtro",
      text: `Os filtros configurados atualmente abrangem ${count} solicitações.

Dependendo do evento escolhido:
• Eventos recorrentes podem processar todas essas solicitações ao longo do tempo.
• Eventos pontuais impactarão apenas a solicitação que disparar o evento.

Revise com atenção.
Esta configuração pode gerar impactos operacionais relevantes e a execução das ações é IRREVERSÍVEL.`,
    };
  }

  if (count > 50) {
    return {
      severity: "error",
      title: "⚠️ Abrangência alta do filtro",
      text: `Os filtros configurados abrangem ${count} solicitações.

Isso representa o universo potencial de aplicação da regra.
O impacto real dependerá do evento selecionado e do momento em que ele ocorrer.

Revise os filtros para garantir que a abrangência está correta.`,
    };
  }

  if (count > 10) {
    return {
      severity: "warning",
      title: "Atenção à abrangência",
      text: `Os filtros configurados abrangem ${count} solicitações.

Esse número indica o alcance potencial da regra.
A execução efetiva dependerá do evento configurado (pontual ou recorrente).`,
    };
  }

  return {
    severity: "info",
    title: "Abrangência controlada",
    text: `Os filtros configurados abrangem ${count} solicitação(ões).

A regra será aplicada conforme o evento selecionado, não necessariamente a todas de forma imediata.`,
  };
};

const compactText = (text, maxLen = 80) => {
  const s = String(text ?? "");
  if (s.length <= maxLen) return s;
  return s.slice(0, maxLen - 1) + "…";
};

const compactListText = (value, maxItems = 2, maxLen = 80) => {
  const arr = Array.isArray(value)
    ? value
    : String(value ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

  if (arr.length === 0) return "";
  const head = arr.slice(0, maxItems).join(", ");
  const tail = arr.length > maxItems ? ` (+${arr.length - maxItems})` : "";
  return compactText(head + tail, maxLen);
};

const chipSx = {
  maxWidth: "100%",
  "& .MuiChip-label": {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  },
};

/* ======== Resumo ao vivo ======== */
function LivePreview({ objeto, evento, condicoesLabels, acoesTitles, eventSchema }) {

  return (
    <Card
      elevation={8}
      sx={{
        position: "sticky",
        top: 16,
        borderRadius: 2,
        boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
        border: "1px solid",
        borderColor: "divider",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(255,255,255,0.98))",
        backdropFilter: "blur(6px)",
      }}
    >
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Resumo ao vivo
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 1 }}
        >
          <InfoOutlinedIcon fontSize="inherit" />
          Pré-visualização
        </Typography>

        <Box sx={{ mt: 1.5 }}>
          <Typography variant="subtitle2">Objeto</Typography>
          <Chip label={objeto || "—"} size="small" sx={{ mt: 0.5 }} />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Evento</Typography>
          <Chip label={evento || "—"} size="small" sx={{ mt: 0.5 }} />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Condições</Typography>
          <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {condicoesLabels && Object.keys(condicoesLabels).length > 0 ? (
              Object.entries(condicoesLabels).map(([k, v]) => {
                const niceKey = eventSchema?.[k]?.label || k;
                const full = `${niceKey}: ${v}`;
                const shown = `${niceKey}: ${compactListText(v, 2, 90) || v}`;

                return (
                  <Tooltip key={k} title={full}>
                    <Chip label={shown} size="small" sx={chipSx} />
                  </Tooltip>
                );

              })
            ) : (
              <Chip label="Nenhuma" size="small" />
            )}
          </Box>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Ações</Typography>
          <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 1 }}>
            {acoesTitles.length === 0 ? (
              <Chip label="Nenhuma" size="small" />
            ) : (
              acoesTitles.map((t, i) => (
                <Box
                  key={`${t}-${i}`}
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    fontSize: 14,
                  }}
                >
                  {i + 1}. {t}
                </Box>
              ))
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function AddAutomationRuleForm({
  rule,
  onClose,
  onSubmit,
  isFetching,
  isLoading,
}) {
  const { userData } = useAuthContext();

  // ===== Estado base
  const initial = useMemo(() => {
    let cfg = {};
    if (rule?.config) {
      try {
        cfg = JSON.parse(rule.config);
      } catch {}
    }
    return {
      id: rule?.id || null,
      descricao: rule?.descricao || "",
      id_objeto: cfg.objeto ?? rule?.id_objeto ?? "",
      id_condicao: cfg.evento ?? rule?.id_condicao ?? "",
      prioridade: cfg.prioridade ?? "",
      fila: rule?.fila ?? cfg.extras?.fila ?? "",
      ordem: rule?.ordem ?? cfg.extras?.ordem ?? "",
      break: rule?.break ?? cfg.extras?.break ?? 0,
      webhook: rule?.webhook ?? cfg.extras?.webhook ?? "",
      token: rule?.token ?? cfg.extras?.token ?? "",
      condicoes: cfg.condicoes ?? {},
      condicoes_labels: cfg.condicoes_labels ?? {},
      acoes: Array.isArray(cfg.acoes) ? cfg.acoes : [], // [{id, values, labels}]
      id_usuario: rule?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
    };
  }, [rule, userData?.id]);

  const [form, setForm] = useState(initial);
  const lastRuleIdRef = useRef(null);

  useEffect(() => {
    const incomingId = initial?.id ?? null;

    // Só re-hidrata quando mudar a regra (ex.: abriu outra ou carregou a primeira)
    if (incomingId !== lastRuleIdRef.current) {
      lastRuleIdRef.current = incomingId;
      setForm(initial);
      setActiveStep(0);
      setErrors({});
      setErrorMessage(null);
    }
  }, [initial]);

  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const topRef = useRef(null);

  const [previewCount, setPreviewCount] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);


  useEffect(() => {
    if (activeStep !== 2) return;
    if (!form.id_objeto || !form.id_condicao) return;

    const hasFilters = Object.keys(form.condicoes || {}).length > 0;
    if (!hasFilters) {
      setPreviewCount(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setPreviewLoading(true);

        const res = await fetchData(
          "/automation-rules/preview",
          "POST",
          {
            id_objeto: Number(form.id_objeto),
            id_condicao: Number(form.id_condicao), // ✅ nome correto
            condicoes: form.condicoes,
          }
        );

        setPreviewCount(res?.data?.total ?? 0);
      } catch {
        setPreviewCount(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 500); // debounce

    return () => clearTimeout(timer);
  }, [activeStep, form.id_objeto, form.id_condicao, form.condicoes]);

  // catálogos
  const { data: metaAll } = useAutomationCatalog();
  const { data: metaByObj } = useAutomationCatalog(form.id_objeto || undefined);

  const objetos = metaAll?.data?.objetos ?? [];
  const eventos = (metaByObj?.data?.condicoes ?? []).filter(
    (c) => c.id_objeto === Number(form.id_objeto)
  );
  const acoesCat = (metaByObj?.data?.acoes ?? []).filter(
    (a) => a.id_objeto === Number(form.id_objeto)
  );

  const selectedObjeto = objetos.find((o) => o.id === Number(form.id_objeto));
  const selectedEvento = eventos.find((e) => e.id === Number(form.id_condicao));

  // ===== schema do evento (para filtros)
  const eventSchema =
    selectedEvento?.form?.condicoes ||
    selectedEvento?.form?.fields ||
    selectedEvento?.form ||
    {};
  const allFilterEntries = Object.entries(eventSchema);
  const usedKeys = new Set(Object.keys(form.condicoes || {}));
  const remainingFilters = allFilterEntries.filter(([k]) => !usedKeys.has(k));

  const DYNAMIC_KEYS = new Set([
    "origensdeleads",
    "usuarios",
    "formasdepagamento",
    "tiposdeleads",
    "subtiposdeleads",
    "subtiposdeleads1",
    "subtiposdeleads2",
    "metodosdeentrega",
    "cargos",
    "positions",
  ]);

  const isDynamicObjectField = (def, key) => {
    if (DYNAMIC_KEYS.has(key)) return true;
    return [
      "origensdeleads",
      "usuarios",
      "formasdepagamento",
      "tiposdeleads",
      "subtiposdeleads",
      "metodosdeentrega",
      "cargos",
      "positions",
    ].includes(def?.object);
  };


  useEffect(() => {
    // precisa ter schema e condições carregadas
    const cond = form?.condicoes || {};
    if (!eventSchema || !Object.keys(eventSchema).length) return;
    if (!cond || !Object.keys(cond).length) return;

    let changed = false;
    const nextLabels = { ...(form.condicoes_labels || {}) };

    Object.entries(cond).forEach(([key, value]) => {
      const def = eventSchema[key];
      if (!def) return;

      if (isDynamicObjectField(def, key)) {
        // não grava label fallback com IDs — deixa pro DynamicFields rehidratar
        return;
      }

      if (isEmptyLabel(nextLabels[key])) {
        const computed = labelFromValue(def, value);
        if (!isEmptyLabel(computed)) {
          nextLabels[key] = computed;
          changed = true;
        }
      }

    });

    if (changed) {
      setForm((f) => ({ ...f, condicoes_labels: nextLabels }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventSchema, form?.condicoes]);


  // ===== utils / validação
  const compactText = (text, maxLen = 80) => {
    const s = String(text ?? "");
    if (s.length <= maxLen) return s;
    return s.slice(0, maxLen - 1) + "…";
  };

  const compactListText = (value, maxItems = 2, maxLen = 80) => {
    // value pode ser string "a, b, c" ou array
    const arr = Array.isArray(value) ? value : String(value ?? "").split(",").map(s => s.trim()).filter(Boolean);
    if (arr.length === 0) return "";
    const head = arr.slice(0, maxItems).join(", ");
    const tail = arr.length > maxItems ? ` (+${arr.length - maxItems})` : "";
    return compactText(head + tail, maxLen);
  };

  const chipSx = {
    maxWidth: "100%",
    "& .MuiChip-label": {
      display: "block",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      maxWidth: "100%",
    },
  };

  const isEmpty = (v) =>
    v == null ||
    (typeof v === "string" && v.trim() === "") ||
    (Array.isArray(v) && v.length === 0);

  const isEmptyLabel = (v) =>
    v == null ||
    (typeof v === "string" && v.trim() === "") ||
    (Array.isArray(v) && v.length === 0);

  const normalizeOptions = (def) => {
    // tenta cobrir formatos comuns
    const raw =
      def?.options ||
      def?.items ||
      def?.choices ||
      def?.values ||
      def?.enum ||
      [];

    if (Array.isArray(raw)) {
      return raw.map((it) => {
        if (it && typeof it === "object") {
          const value = it.value ?? it.id ?? it.key ?? it.code ?? it;
          const label = it.label ?? it.nome ?? it.name ?? it.title ?? String(value);
          return { value, label };
        }
        return { value: it, label: String(it) };
      });
    }

    // caso venha como objeto {value: label}
    if (raw && typeof raw === "object") {
      return Object.entries(raw).map(([value, label]) => ({ value, label: String(label) }));
    }

    return [];
  };

  const toArrayValue = (value) => {
    if (value == null) return [];
    if (Array.isArray(value)) return value;

    // se vier string tipo "9,8,7" ou "9;8;7"
    if (typeof value === "string") {
      const s = value.trim();
      if (!s) return [];
      if (s.includes(",") || s.includes(";")) {
        return s
          .split(/[;,]/g)
          .map((t) => t.trim())
          .filter(Boolean);
      }
      return [s];
    }

    // number/bool/etc
    return [value];
  };

  const labelFromValue = (def, value) => {
    const opts = normalizeOptions(def);

    const findOne = (v) => {
      const hit = opts.find((o) => String(o.value) === String(v));
      return hit ? hit.label : String(v);
    };

    const arr = toArrayValue(value);

    // se o campo NÃO é múltiplo e veio só 1 valor, devolve string simples
    if (!def?.multiple && arr.length <= 1) return arr.length ? findOne(arr[0]) : "";

    // múltiplo -> devolve string com nomes
    return arr.map(findOne).join(", ");
  };


  const validateStep = (stepIdx) => {
    const e = {};
    const msgs = [];

    if (stepIdx === 0) {
      if (!form.descricao) {
        e.descricao = "Informe uma descrição.";
        msgs.push('Falta "Descrição".');
      }
      if (!form.id_objeto) {
        e.id_objeto = "Selecione o objeto.";
        msgs.push('Falta selecionar o "Objeto".');
      }
    }

    if (stepIdx === 1) {
      if (!form.id_condicao) {
        e.id_condicao = "Selecione o evento.";
        msgs.push('Falta selecionar o "Evento".');
      }
    }

    if (stepIdx === 2) {
      const cond = form.condicoes || {};
      const keys = Object.keys(cond);

      // 🚫 REGRA PRINCIPAL: precisa ter ao menos 1 condição
      if (keys.length === 0) {
        msgs.push("Adicione ao menos um filtro para continuar.");
      }

      // valida filtros obrigatórios do schema
      Object.entries(eventSchema).forEach(([key, def]) => {
        if (!(key in cond)) return;
        if (def?.required && isEmpty(cond[key])) {
          const label = def?.label || key;
          msgs.push(`Preencha o filtro obrigatório "${label}".`);
        }
      });
    }

    if (stepIdx === 3) {
      if (!form.acoes?.length) {
        e.acoes = "Inclua ao menos uma ação.";
        msgs.push("Inclua ao menos uma ação.");
      } else {
        form.acoes.forEach((a, idx) => {
          const def = acoesCat.find((x) => x.id === Number(a.id));
          const title = def?.nome || `Ação #${a.id || "?"}`;
          const schemaA = def?.form?.fields || def?.form?.acoes || def?.form || {};
          Object.entries(schemaA).forEach(([k, f]) => {
            if (f?.required && isEmpty(a.values?.[k])) {
              const label = f?.label || k;
              msgs.push(`Ação ${idx + 1} (${title}): preencha "${label}".`);
            }
          });
        });
      }
    }

    setErrors(e);
    setErrorMessage(msgs.length ? msgs.join("\n") : null);
    return !Object.keys(e).length && msgs.length === 0;
  };

  // ===== navegação
  const tryGoTo = (nextIdx) => {
    const ok = validateStep(activeStep);
    if (!ok) {
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    setErrorMessage(null);
    setActiveStep(nextIdx);
  };

  const handleNext = () => {
    if (activeStep >= steps.length - 1) return;
    tryGoTo(activeStep + 1);
  };
  const handleBack = () => {
    if (activeStep === 0) return;
    setActiveStep((s) => Math.max(0, s - 1));
    setErrorMessage(null);
  };

  // ===== salvar (só no clique)
  const handleSubmit = async () => {
    // valida todas as etapas antes de salvar
    const okAll =
      validateStep(0) && validateStep(1) && validateStep(2) && validateStep(3);
    if (!okAll) {
      if (errors.id_objeto || errors.descricao) setActiveStep(0);
      else if (errors.id_condicao) setActiveStep(1);
      else if (errors.acoes) setActiveStep(3);
      else setActiveStep(2);
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    const payload = {
      id: form.id || undefined,
      descricao: form.descricao,
      id_usuario: form.id_usuario,
      id_usuario_ultima_alteracao: form.id_usuario_ultima_alteracao,
      id_objeto: Number(form.id_objeto),
      id_condicao: Number(form.id_condicao),
      prioridade: form.prioridade ? Number(form.prioridade) : null,
      fila: form.fila ? Number(form.fila) : null,
      ordem: form.ordem ? Number(form.ordem) : null,
      break: form.break ? Number(form.break) : 0,
      webhook: form.webhook || null,
      token: form.token || null,
      condicoes: form.condicoes || {},
      acoes: (form.acoes || []).map((a) => ({
        id: Number(a.id),
        values: a.values || {},
      })),
    };

    try {
      setSubmitting(true);
      await onSubmit(payload);
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Erro ao salvar a regra.");
    } finally {
      setSubmitting(false);
    }
  };

  // ===== meta para UI
  const stepMeta = {
    condicoes: Object.keys(form.condicoes || {}).length,
    acoes: form.acoes?.length || 0,
  };

  // menu "adicionar filtro"
  const [anchorAdd, setAnchorAdd] = useState(null);
  const openAdd = Boolean(anchorAdd);
  const handleOpenAdd = (e) => setAnchorAdd(e.currentTarget);
  const handleCloseAdd = () => setAnchorAdd(null);

  const addFilter = (key, def) => {
    setForm((f) => ({
      ...f,
      condicoes: { ...(f.condicoes || {}), [key]: def?.multiple ? [] : "" },
      condicoes_labels: { ...(f.condicoes_labels || {}), [key]: "" },
    }));
    handleCloseAdd();
  };

  const removeFilter = (key) =>
    setForm((f) => {
      const next = { ...(f.condicoes || {}) };
      const nextL = { ...(f.condicoes_labels || {}) };
      delete next[key];
      delete nextL[key];
      return { ...f, condicoes: next, condicoes_labels: nextL };
    });

  // menu "adicionar ação"
  const [anchorAddAction, setAnchorAddAction] = useState(null);
  const openAddAction = Boolean(anchorAddAction);
  const handleOpenAddAction = (e) => setAnchorAddAction(e.currentTarget);
  const handleCloseAddAction = () => setAnchorAddAction(null);
  const addAction = (id) => {
    setForm((f) => ({
      ...f,
      acoes: [...(f.acoes || []), { id, values: {}, labels: {} }],
    }));
    handleCloseAddAction();
  };

  const acoesTitles = useMemo(
    () =>
      (form.acoes || []).map((a) => {
        const def = acoesCat.find((x) => x.id === Number(a.id));
        return def?.nome || `Ação #${a.id}`;
      }),
    [form.acoes, acoesCat]
  );

  /* ======================== UI ======================== */
  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 3 },
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div ref={topRef} />
      <Typography variant="h6" gutterBottom>
        {form.id ? "Editar Regra de Automação" : "Adicionar Regra de Automação"}
      </Typography>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
          {errorMessage}
        </Alert>
      )}

      <Grid
        container
        spacing={2}
        sx={{
          maxWidth: "100%",
          overflowX: "hidden",
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        {/* principal */}
        <Grid item xs={12} md={8} sx={{ minWidth: 0 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
            {steps.map((label, idx) => {
              let extra = "";
              if (idx === 2 && stepMeta.condicoes) extra = ` • ${stepMeta.condicoes} filtro(s)`;
              if (idx === 3 && stepMeta.acoes) extra = ` • ${stepMeta.acoes} ação(ões)`;
              return (
                <Step key={label}>
                  <StepLabel>
                    {label}
                    {extra}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>

          {/* IMPORTANTE: sem <form> para não submeter automaticamente */}
          <Box
            onKeyDown={(e) => {
              // Nunca deixa Enter submeter ou acionar "Salvar" implicitamente.
              if (e.key === "Enter") e.preventDefault();
            }}
          >
            {/* ===== 0 - Objeto ===== */}
            {activeStep === 0 && (
              <>
                <Alert icon={<InfoOutlinedIcon />} severity="info" sx={{ mb: 2 }}>
                  Dê um <b>nome</b> para a regra e escolha o <b>Objeto</b> que ela monitora.
                </Alert>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Descrição"
                      size="small"
                      margin="dense"
                      fullWidth
                      value={form.descricao}
                      onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
                      error={!!errors.descricao}
                      helperText={errors.descricao}
                      required
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      select
                      label="Objeto"
                      size="small"
                      margin="dense"
                      fullWidth
                      value={form.id_objeto || ""}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          id_objeto: e.target.value,
                          id_condicao: "",
                          condicoes: {},
                          condicoes_labels: {},
                          acoes: [],
                        }))
                      }
                      error={!!errors.id_objeto}
                      helperText={errors.id_objeto}
                      required
                    >
                      {objetos.map((o) => (
                        <MenuItem key={o.id} value={o.id}>
                          {o.nome}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </>
            )}

            {/* ===== 1 - Evento ===== */}
            {activeStep === 1 && (
              <>
                <Alert icon={<InfoOutlinedIcon />} severity="info" sx={{ mb: 2 }}>
                  Escolha o <b>Evento</b> que dispara a automação. Depois você poderá restringir com
                  filtros (próximo passo).
                </Alert>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      select
                      label="Evento"
                      size="small"
                      margin="dense"
                      fullWidth
                      value={form.id_condicao || ""}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          id_condicao: e.target.value,
                          condicoes: {},
                          condicoes_labels: {},
                        }))
                      }
                      error={!!errors.id_condicao}
                      helperText={errors.id_condicao}
                      required
                    >
                      {eventos.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.nome}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    {(selectedEvento?.descricao || selectedEvento?.help) && (
                      <Alert icon={<InfoOutlinedIcon />} severity="success">
                        {(selectedEvento?.descricao || selectedEvento?.help || "").toString()}
                      </Alert>
                    )}
                  </Grid>
                </Grid>
              </>
            )}

            {/* ===== 2 - Condições ===== */}
            {activeStep === 2 && (
              <>
                <Alert icon={<TuneIcon />} severity="info" sx={{ mb: 2 }}>
                  Adicione os <b>filtros</b> que limitam quando a regra deve disparar. Você pode
                  incluir vários — apenas os necessários.
                </Alert>

                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddCircleOutline />}
                    onClick={handleOpenAdd}
                    disabled={remainingFilters.length === 0}
                  >
                    Adicionar filtro
                  </Button>
                  {remainingFilters.length === 0 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ alignSelf: "center" }}
                    >
                      Todos os filtros disponíveis já foram adicionados.
                    </Typography>
                  )}
                </Box>

                <Menu open={openAdd} onClose={handleCloseAdd} anchorEl={anchorAdd}>
                  {remainingFilters.map(([key, def]) => (
                    <MItem key={key} onClick={() => addFilter(key, def)}>
                      <ListItemIcon>
                        <DoneAllIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={def?.label || key}
                        secondary={def?.helperText || def?.hint || ""}
                      />
                    </MItem>
                  ))}
                </Menu>

                {Object.keys(form.condicoes || {}).length === 0 && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Esta regra <b>exige ao menos um filtro</b> para ser criada com segurança.
                    <br />
                    Adicione um ou mais filtros para continuar.
                  </Alert>
                )}

                {Object.entries(form.condicoes || {}).map(([key, val], idx) => {
                  const def = eventSchema[key] || { label: key, type: "text" };
                  const titulo = def.label || key;

                  return (
                    <Accordion key={key} defaultExpanded sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                          }}
                        >
                          <Typography fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
                            {idx + 1}. {titulo}
                          </Typography>
                          {form.condicoes_labels?.[key] && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            >
                              —
                              {Array.isArray(form.condicoes_labels[key])
                                ? ` ${form.condicoes_labels[key].join(", ")}`
                                : ` ${form.condicoes_labels[key]}`}
                            </Typography>
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <DynamicFields
                          compact
                          schema={{ fields: { [key]: def } }}
                          schemaFull={{ fields: eventSchema }}
                          onlyKeys={[key]}
                          values={{ ...form.condicoes }}
                          formState={form}
                          onChange={(name, value) =>
                            setForm((f) => ({
                              ...f,
                              condicoes: { ...(f.condicoes || {}), [name]: value },
                            }))
                          }
                          onLabel={(name, label) =>
                            setForm((f) => ({
                              ...f,
                              condicoes_labels: {
                                ...(f.condicoes_labels || {}),
                                [name]: label,
                              },
                            }))
                          }
                        />
                        <Box sx={{ mt: 1 }}>
                          <Button
                            color="error"
                            size="small"
                            startIcon={<DeleteOutline />}
                            onClick={() => removeFilter(key)}
                          >
                            Remover filtro
                          </Button>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}

                <Card
                  variant="outlined"
                  sx={{
                    mb: 2,
                    borderLeft: "4px solid",
                    borderColor: "primary.main",
                    bgcolor: "background.paper",
                  }}
                >
                <CardContent sx={{ py: 1.5 }}>
                    <Typography variant="subtitle2">
                      Prévia de impacto
                    </Typography>

                    {previewLoading ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">
                          Calculando impacto…
                        </Typography>
                      </Box>
                    ) : previewCount != null ? (() => {
                      const alert = getImpactAlert(previewCount);

                      return (
                        <Alert severity={alert.severity}>
                          <Typography fontWeight={600}>{alert.title}</Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {alert.text}
                          </Typography>
                        </Alert>
                      );
                    })() : (
                      <Typography variant="body2" color="text.secondary">
                        Defina filtros para ver a prévia.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </>
            )}

            {/* ===== 3 - Ações ===== */}
            {activeStep === 3 && (
              <>
                <Alert icon={<BoltIcon />} severity="info" sx={{ mb: 2 }}>
                  Inclua uma ou mais <b>ações</b>. Você pode reordenar, duplicar e remover.
                </Alert>

                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddCircleOutline />}
                    onClick={handleOpenAddAction}
                    disabled={!acoesCat.length}
                  >
                    Adicionar ação
                  </Button>
                </Box>

                <Menu
                  open={openAddAction}
                  onClose={handleCloseAddAction}
                  anchorEl={anchorAddAction}
                >
                  {acoesCat.map((a) => (
                    <MItem key={a.id} onClick={() => addAction(a.id)}>
                      <ListItemIcon>
                        <DoneAllIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={a.nome} secondary={a?.descricao || ""} />
                    </MItem>
                  ))}
                </Menu>

                {(form.acoes || []).length === 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Inclua ao menos uma ação (ex.: “Enviar email”).
                  </Alert>
                )}

                {(form.acoes || []).map((a, idx) => {
                  const def = acoesCat.find((x) => x.id === Number(a.id));
                  const title = def?.nome || `Ação #${a.id}`;

                  const pairs = Object.entries(a.labels || {});
                  const resumo = pairs.length
                    ? pairs
                        .slice(0, 2)
                        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
                        .join(" • ")
                    : "sem parâmetros";

                  return (
                    <Accordion key={idx} defaultExpanded={false} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            minWidth: 0,
                          }}
                        >
                          <Typography fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
                            {idx + 1}. {title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                          >
                            — {resumo}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box
                          display="flex"
                          gap={1}
                          alignItems="center"
                          sx={{ mb: 1, flexWrap: "wrap" }}
                        >
                          <TextField
                            select
                            label="Ação"
                            size="small"
                            margin="dense"
                            sx={{ minWidth: 260 }}
                            value={a.id}
                            onChange={(e) => {
                              const id = Number(e.target.value);
                              setForm((f) => {
                                const copy = [...f.acoes];
                                copy[idx] = { id, values: {}, labels: {} };
                                return { ...f, acoes: copy };
                              });
                            }}
                          >
                            {acoesCat.map((x) => (
                              <MenuItem key={x.id} value={x.id}>
                                {x.nome}
                              </MenuItem>
                            ))}
                          </TextField>

                          <Tooltip title="Duplicar">
                            <IconButton
                              onClick={() =>
                                setForm((f) => {
                                  const copy = [...(f.acoes || [])];
                                  copy.splice(idx + 1, 0, JSON.parse(JSON.stringify(a)));
                                  return { ...f, acoes: copy };
                                })
                              }
                            >
                              <FileCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Mover para cima">
                            <span>
                              <IconButton
                                disabled={idx === 0}
                                onClick={() =>
                                  setForm((f) => {
                                    const copy = [...(f.acoes || [])];
                                    const [it] = copy.splice(idx, 1);
                                    copy.splice(idx - 1, 0, it);
                                    return { ...f, acoes: copy };
                                  })
                                }
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title="Mover para baixo">
                            <span>
                              <IconButton
                                disabled={idx === (form.acoes || []).length - 1}
                                onClick={() =>
                                  setForm((f) => {
                                    const copy = [...(f.acoes || [])];
                                    const [it] = copy.splice(idx, 1);
                                    copy.splice(idx + 1, 0, it);
                                    return { ...f, acoes: copy };
                                  })
                                }
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title="Remover">
                            <IconButton
                              color="error"
                              onClick={() =>
                                setForm((f) => ({
                                  ...f,
                                  acoes: f.acoes.filter((_, i) => i !== idx),
                                }))
                              }
                            >
                              <DeleteOutline />
                            </IconButton>
                          </Tooltip>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {def?.form ? (
                          <DynamicFields
                            compact
                            schema={def.form}
                            values={a.values || {}}
                            formState={form}
                            onChange={(name, value) =>
                              setForm((f) => {
                                const copy = [...f.acoes];
                                copy[idx] = {
                                  ...copy[idx],
                                  values: {
                                    ...(copy[idx].values || {}),
                                    [name]: value,
                                  },
                                };
                                return { ...f, acoes: copy };
                              })
                            }
                            onLabel={(name, label) =>
                              setForm((f) => {
                                const copy = [...f.acoes];
                                copy[idx] = {
                                  ...copy[idx],
                                  labels: {
                                    ...(copy[idx].labels || {}),
                                    [name]: label,
                                  },
                                };
                                return { ...f, acoes: copy };
                              })
                            }
                          />
                        ) : (
                          <Alert severity="info">Esta ação não possui parâmetros.</Alert>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}

                {errors.acoes && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {errors.acoes}
                  </Alert>
                )}
              </>
            )}

            {/* ===== 4 - Resumo (legível) ===== */}
            {activeStep === 4 && (
              <Box>
                <Alert icon={<InfoOutlinedIcon />} severity="success" sx={{ mb: 2 }}>
                  Revise abaixo e clique em <b>Salvar</b>.
                </Alert>

                <Box sx={{ display: "grid", gap: 2 }}>
                  <Box>
                    <Typography variant="subtitle2">Descrição</Typography>
                    <Typography>{form.descricao || "—"}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2">Objeto</Typography>
                    <Chip label={selectedObjeto?.nome || "—"} size="small" />
                  </Box>

                  <Box>
                    <Typography variant="subtitle2">Evento</Typography>
                    <Chip label={selectedEvento?.nome || "—"} size="small" />
                  </Box>

                  <Box>
                    <Typography variant="subtitle2">Condições</Typography>

                    <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {Object.keys(form.condicoes_labels || {}).length ? (
                        Object.entries(form.condicoes_labels).map(([k, v]) => {
                          const niceKey = eventSchema?.[k]?.label || k;
                          const full = Array.isArray(v) ? `${niceKey}: ${v.join(", ")}` : `${niceKey}: ${v}`;
                          const short = compactListText(v, 2, 90);
                          const shown = short ? `${niceKey}: ${short}` : `${niceKey}: —`;

                          return (
                            <Tooltip key={k} title={full}>
                              <Chip label={shown} size="small" sx={chipSx} />
                            </Tooltip>
                          );
                        })
                      ) : (
                        <Chip label="Nenhuma" size="small" />
                      )}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2">Ações</Typography>
                    <Box sx={{ display: "grid", gap: 1, mt: 0.5 }}>
                      {(form.acoes || []).length ? (
                        form.acoes.map((a, i) => {
                          const def = acoesCat.find((x) => x.id === Number(a.id));
                          const title = def?.nome || `Ação #${a.id}`;
                          const pairs = Object.entries(a.labels || {});
                          const resumo = pairs.length
                            ? pairs
                                .map(([k, v]) =>
                                  Array.isArray(v) ? `${k}: ${v.join(", ")}` : `${k}: ${v}`
                                )
                                .join(" • ")
                            : "sem parâmetros";

                          return (
                            <Box
                              key={`${a.id}-${i}`}
                              sx={{
                                px: 1.25,
                                py: 0.75,
                                borderRadius: 1,
                                bgcolor: "action.hover",
                              }}
                            >
                              <b>
                                {i + 1}. {title}
                              </b>
                              <Typography variant="body2" color="text.secondary">
                                {resumo}
                              </Typography>
                            </Box>
                          );
                        })
                      ) : (
                        <Chip label="Nenhuma" size="small" />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {/* navegação */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                mt: 3,
              }}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={isFetching || submitting || isLoading}
              >
                Cancelar
              </Button>
              <Box display="flex" gap={2}>
                {activeStep > 0 && <Button onClick={handleBack}>Voltar</Button>}
                {activeStep < steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={isFetching || submitting || isLoading}
                  >
                    Próximo
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    disabled={isFetching || submitting || isLoading}
                  >
                    {isFetching || submitting || isLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      "Salvar"
                    )}
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Grid>

        {/* preview */}
        <Grid item xs={12} md={4} sx={{ width: "100%", flexShrink: 0, pr: 0 }}>
          <LivePreview
            objeto={selectedObjeto?.nome}
            evento={selectedEvento?.nome}
            condicoesLabels={form.condicoes_labels}
            acoesTitles={acoesTitles}
            eventSchema={eventSchema}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}
